import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  GeoJSON,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "../styles/Geothermal.css";
import airportData from "./Airport.json";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

// ✅ Separate icons for Airport and Heliport
const airportIcon = new L.Icon({
  iconUrl: "/img/airport2.png",
  iconSize: [35, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -30],
});

const heliportIcon = new L.Icon({
  iconUrl: "/img/heliport.png", // 👉 Add heliport icon in public/img/
  iconSize: [35, 41],
  iconAnchor: [12, 41],
  popupAnchor: [0, -30],
});

const isMobileView = () => window.innerWidth <= 778;

const MapFocus = ({ location }) => {
  const map = useMap();
  if (location) {
    map.flyTo(location, isMobileView() ? 5 : 10);
  }
  return null;
};

const Airports = () => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [utahBoundary, setUtahBoundary] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const markerRefs = useRef([]);

  useEffect(() => {
    fetch("/utah-boundary.geojson")
      .then((response) => response.json())
      .then((data) => setUtahBoundary(data))
      .catch((error) => console.error("Error loading Utah boundary:", error));
  }, []);

  const handleLocationClick = (location, index) => {
    setSelectedLocation(location);
    if (markerRefs.current[index]) {
      markerRefs.current[index].openPopup();
    }
  };

  // 🔍 Filter
  const filteredData = airportData.filter(
    (spot) =>
      spot.CITY_NAME.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.FULLNAME.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.COUNTY_NAM.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.FAC_CYSTZP.toLowerCase().includes(searchTerm.toLowerCase()) ||
      spot.ICAO.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <LandingSection
        data={{
          imageName: "airport.webp",
          title: "Utah Airports",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah’s Airports ({airportData.length})</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah’s airport system, led by Salt Lake City International,
              supports statewide travel and connectivity through key regional
              hubs.
            </p>
            <Link to={"/about"}></Link>
          </div>
        </div>
      </div>

      <div className="geothermal-map">
        <div className="list-container">
          <h2 className="flex items-center justify-center">
            Airports and Heliports ({airportData.length})
          </h2>

          <div className="flex justify-end">
            <a
              target="_blank"
              href="https://gis-support-utah-em.hub.arcgis.com/datasets/utah::utah-airport-locations/explore"
              rel="noreferrer"
            >
              Airports and Heliports Source
            </a>
          </div>

          <div className="flex justify-center items-center my-4">
            <input
              type="text"
              placeholder="Search by City, Airport, County, ZipCode or ICAO code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input p-2 border border-gray-300 rounded-md w-[40rem]"
            />
          </div>

          {/* Table Header */}
          <div className="list-item-content rounded-lg border-pink-700 hover:border-1 border-solid p-4 transition-all duration-200 bg-red-200/50">
            <span className="text-red-800 font-bold">City</span>
            <span className="text-red-800 font-bold">Airport</span>
            <span className="text-red-800 font-bold">County</span>
            <span className="text-red-800 font-bold">City/ZipCode</span>
            <span className="text-red-800 font-bold">ICAO</span>
            <span className="text-red-800 font-bold">Type</span>
          </div>

          {/* Scrollable List */}
          <div className="list-scroll max-h-[630px] overflow-y-auto mt-2">
            <ul>
              {filteredData.map((spot, index) => (
                <li
                  key={index}
                  className="list-item"
                  onClick={() =>
                    handleLocationClick(
                      {
                        lat: spot.LATITUDE,
                        lng: spot.LONGITUDE,
                        details: spot,
                      },
                      index
                    )
                  }
                >
                  <div className="list-item-content text-blue-800 font-bold">
                    <span>{spot.CITY_NAME}</span>
                    <span>{spot.FULLNAME}</span>
                    <span>{spot.COUNTY_NAM}</span>
                    <span>{spot.FAC_CYSTZP}</span>
                    <span>{spot.ICAO}</span>
                    <span>
                      <img
                        src={
                          spot.LAN_FA_TY === "HELIPORT"
                            ? "/img/heliport.png"
                            : "/img/airport2.png"
                        }
                        alt={spot.LAN_FA_TY}
                        title={spot.LAN_FA_TY}
                        className="w-6 h-6"
                      />
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="map-container">
          <MapContainer
            center={[39.32098, -111.093731]}
            zoom={7.2}
            minZoom={6}
            maxBounds={[
              [30, -105],
              [50, -120],
            ]}
            maxBoundsViscosity={1.0}
            className="map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />

            {selectedLocation && (
              <MapFocus
                location={[selectedLocation.lat, selectedLocation.lng]}
              />
            )}

            {utahBoundary && (
              <GeoJSON
                data={utahBoundary}
                style={() => ({
                  color: "blue",
                  weight: 2,
                  opacity: 1,
                  fillColor: "lightblue",
                  fillOpacity: 0.2,
                })}
              />
            )}

            {filteredData.map((spot, index) => (
              <Marker
                key={index}
                position={[spot.LATITUDE, spot.LONGITUDE]}
                icon={
                  spot.LAN_FA_TY === "HELIPORT" ? heliportIcon : airportIcon
                }
                ref={(el) => (markerRefs.current[index] = el)}
              >
                <Popup>
                  <b>
                    <u>
                      {spot.ICAO} - {spot.FULLNAME}
                    </u>
                  </b>
                  <br />
                  Type: {spot.LAN_FA_TY} <br />
                  Site no.: {spot.SITE_NO} <br />
                  State: {spot.STATE_NAME} <br />
                  County: {spot.COUNTY_NAM} <br />
                  City: {spot.CITY_NAME} <br />
                  Elevation: {spot.ELEV} <br />
                  Aero Chart: {spot.AERO_CHART} <br />
                  Area/Zipcode: {spot.FAC_CYSTZP} <br />
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Airports;
